import { AlertTriangle, Download } from "lucide-react";
import { useEffect, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { toast } from "@/shared/lib/toast";
import { Button } from "@/shared/ui/button";
import { Spinner } from "@/shared/ui/spinner";

import { useAuditExport } from "../api/use-audit-export";
import type { AuditFilterState } from "../lib/url-state";

interface AuditExportButtonProps {
  /** Task 1's URL-derived filter state — exported verbatim via Task 2's
   * useAuditExport (which builds the request from it), never re-derived
   * here. */
  filter: AuditFilterState;
}

type ExportState = "idle" | "starting" | "queued" | "running" | "done" | "failed";

const BUSY_STATES: ReadonlySet<ExportState> = new Set(["starting", "queued", "running"]);

/**
 * Single button driving Task 2's async export job end to end: kicks off the
 * job for the ACTIVE filters, reflects queued/running progress, renders the
 * backend's own presigned download URL once done, and surfaces failure as a
 * toast — covering both a `status: "failed"` job (soft failure) and
 * `pollError` (hard failure — the status poll itself gave up, so no job
 * ever reached "failed"). Never builds or guesses the CSV or its URL
 * client-side (plan constraint: export is a real backend job).
 *
 * `job.error` (backend/infra error text, e.g. raw AWS SDK output) is
 * intentionally never interpolated into the toast — it's not meant for
 * end users. All three failure toasts show the same generic, safe copy.
 */
export function AuditExportButton({ filter }: AuditExportButtonProps): JSX.Element {
  const { t } = useTranslation("audit");
  const { startExport, isStarting, job, pollError, startError } = useAuditExport();

  // "custom" with an incomplete range can't be turned into a real request —
  // auditFilterToQuery would silently substitute its own 7-day default,
  // exporting a range the filter bar isn't even showing. Block instead.
  // (Task 1's url-state.ts already rejects a malformed date string down to
  // `undefined`; ordering, e.g. date_from after date_to, is out of scope —
  // same as the filter UI itself.)
  const isDateRangeInvalid =
    filter.preset === "custom" && (filter.date_from === undefined || filter.date_to === undefined);

  const state: ExportState =
    job?.status === "done"
      ? "done"
      : job?.status === "failed" || Boolean(pollError) || Boolean(startError)
        ? "failed"
        : job?.status === "running"
          ? "running"
          : job?.status === "queued"
            ? "queued"
            : isStarting
              ? "starting"
              : "idle";

  // Toasts are the failure UI (brief requirement) — fire once per new
  // failure via the effect's dependency array (a fresh pollError/job.status
  // reference only shows up on a genuinely new failed attempt).
  useEffect(() => {
    if (pollError) {
      toast.error(t("export.toast_poll_failed"));
    }
  }, [pollError, t]);

  useEffect(() => {
    if (startError) {
      toast.error(t("export.toast_start_failed"));
    }
  }, [startError, t]);

  useEffect(() => {
    if (job?.status === "failed") {
      // job.error carries raw backend/infra error text (e.g. AWS SDK
      // internals) — never fit for end users. Same generic copy as the
      // pollError/startError toasts above, not a fourth style.
      toast.error(t("export.toast_failed"));
    } else if (job?.status === "done") {
      toast.success(t("export.toast_done", { count: job.row_count ?? 0 }));
    }
  }, [job?.status, job?.row_count, t]);

  const isBusy = BUSY_STATES.has(state);
  // Narrowed through an explicit `job !== undefined` check (rather than
  // `job?.download_url`) so `downloadUrl`/`rowCount` are usable below without
  // re-deriving `job` is defined at every access site.
  const downloadUrl = job !== undefined && job.status === "done" ? job.download_url : undefined;
  const rowCount = job !== undefined && job.status === "done" ? (job.row_count ?? 0) : 0;
  const showDownload = downloadUrl !== undefined;

  return (
    <div className="flex items-center gap-2" aria-live="polite">
      <Button
        type="button"
        variant={showDownload ? "outline" : "default"}
        size="sm"
        onClick={() => startExport(filter)}
        disabled={isDateRangeInvalid || isBusy}
        aria-label={
          isDateRangeInvalid ? t("export.disabled_invalid_range") : t(`export.aria_${state}`)
        }
        title={isDateRangeInvalid ? t("export.disabled_invalid_range") : undefined}
        data-testid="audit-export-button"
        data-export-state={state}
        className="gap-2"
      >
        {isBusy ? (
          <Spinner size="sm" className="h-4 w-4" label={t(`export.aria_${state}`)} />
        ) : state === "failed" ? (
          <AlertTriangle className="h-4 w-4" aria-hidden />
        ) : (
          <Download className="h-4 w-4" aria-hidden />
        )}
        {t(`export.label_${state}`)}
      </Button>

      {showDownload && (
        <Button asChild variant="default" size="sm" className="gap-2">
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="audit-export-download"
            aria-label={t("export.download", { count: rowCount })}
          >
            <Download className="h-4 w-4" aria-hidden />
            {t("export.download", { count: rowCount })}
          </a>
        </Button>
      )}
    </div>
  );
}
